import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  // `whitespace-nowrap`: a pill is a single-line affordance — two-word labels
  // ("Closed Lost", "Bid Requested", "Awaiting Signature") must never wrap to
  // a second line inside the pill, which reads as broken. The badge keeps its
  // intrinsic width; the surrounding layout (e.g. a table column) accommodates
  // it rather than squeezing the text.
  "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors",
  {
    variants: {
      variant: {
        default: "border-border bg-surface-elevated text-muted-foreground",
        primary: "border-primary-mid bg-primary-dim text-primary",
        warning: "border-warning-mid bg-warning-dim text-warning",
        success: "border-success-dim bg-success-dim text-success",
        // Blue "in flight / staged" tone — a neutral-informational state that
        // is neither a warning nor a completion. Distinct at a glance from the
        // green `success` when the two are interleaved in one list.
        info: "border-info-dim bg-info-dim text-info",
        destructive: "border-destructive-dim bg-destructive-dim text-destructive",
        // Solid accent fill. Uses `primary-fill` (the token reserved for solid
        // surfaces) paired with `primary-foreground`, the same pairing CTA
        // buttons use — so it stays legible in every palette. A bright
        // `bg-primary` fill is reserved for text/icons and goes illegible under
        // white text in light-primary palettes.
        solid: "border-transparent bg-primary-fill text-primary-foreground",
        // Solid gold "action" accent — the highlight the design system reserves
        // for the chosen/recommended item (teal is brand; orange is action).
        // Solid gold fill with the gold-hue's dark foreground so it stays
        // legible in every palette.
        solidAction: "border-transparent bg-orange text-warning-foreground",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

/**
 * forwardRef matters: Radix `TooltipTrigger asChild` (and `Popover`,
 * `DropdownMenu`, etc.) use `@radix-ui/react-slot` which forwards refs to
 * the underlying DOM node. Without this, wrapping a Badge in a Radix
 * trigger silently drops the ref and the tooltip/popover never fires.
 */
export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  ),
);
Badge.displayName = "Badge";

export { badgeVariants };
